/**
 * #711 — shared machine-code → human-copy error mapper.
 *
 * The server's dominant error contract is `{ error: '<snake_case_code>' }`
 * (rbac middleware, global handler, most module routes); other modules
 * emit prose in `error` (booking) or `{ error: code, message: prose }`
 * (rate-limit). Pre-fix, both frontends copied `body.error` verbatim into
 * render state — a logged-in owner saw the literal string `invalid_token`
 * on /schedule, and a customer saw `internal_server_error` in the
 * agreement modal.
 *
 * This module is the ONE place machine codes become sentences:
 *   - `isMachineCode(s)` — detects the code shape (snake_case, plus the
 *     bare single-word codes the server actually emits).
 *   - `errorCopy(input)` — prose passes through UNTOUCHED; machine codes
 *     map to dictionary copy (en/es) or a safe generic fallback. It can
 *     NEVER return a machine-shaped string for a machine-shaped input.
 *
 * CRITICAL CONSTRAINT: this must work WITHOUT i18next being initialized
 * (the admin app may not init it, and lib/api.ts modules load before any
 * `initI18n()` call). So the dictionaries are read straight from the
 * JSON bundles, and language resolution only CONSULTS the i18next
 * singleton when it happens to be initialized — falling back to the
 * persisted `ferro.lang` preference, then 'en'.
 *
 * The dictionary lives under the `errors.*` namespace of
 * packages/core/i18n/{en,es}.json — the bundle-integrity test enforces
 * en/es key parity, so a code can't gain English copy without Spanish.
 */

import i18next from 'i18next';
import en from './en.json';
import es from './es.json';

export type ErrorCopyLanguage = 'en' | 'es';

/**
 * Snake_case machine-code shape: at least two lowercase/digit segments
 * joined by underscores (`invalid_token`, `no_capacity`, …).
 */
const MACHINE_CODE_RE = /^[a-z0-9]+(?:_[a-z0-9]+)+$/;

/**
 * Single-word codes the server emits with no underscore — they'd slip
 * past the regex but are every bit as raw on screen.
 */
const BARE_MACHINE_CODES: ReadonlySet<string> = new Set([
  'forbidden',
  'unauthorized',
  'unauthenticated',
  'timeout',
]);

/**
 * True when `s` looks like a machine error code, not human prose.
 *
 * BARE single-word codes match case-insensitively ('Forbidden' /
 * 'FORBIDDEN' → 'forbidden'): legacy server paths emit them capitalized
 * and origin/main's humanizeApiError lowercased before matching — losing
 * that cost a real regression ('Forbidden' rendering literally). Only the
 * single-word set gets the lowercase treatment; snake_case codes are
 * lowercase by the regex's definition, and prose sentences never contain
 * a lone bare word, so no prose is ever case-folded.
 */
export function isMachineCode(s: string): boolean {
  const t = s.trim();
  return MACHINE_CODE_RE.test(t) || BARE_MACHINE_CODES.has(t.toLowerCase());
}

type ErrorDict = Record<string, string>;

const DICTS: Record<ErrorCopyLanguage, ErrorDict> = {
  en: ((en as Record<string, unknown>).errors ?? {}) as ErrorDict,
  es: ((es as Record<string, unknown>).errors ?? {}) as ErrorDict,
};

/**
 * Absolute last-resort copy, used only if the JSON bundles somehow lack
 * `errors.generic` (the bundle-integrity test makes that unreachable in
 * practice — this keeps the function total anyway).
 */
const HARD_FALLBACK: Record<ErrorCopyLanguage, string> = {
  en: 'Something went wrong. Please try again.',
  es: 'Algo salió mal. Inténtalo de nuevo.',
};

/**
 * Mirrors STORAGE_KEY in ./index.ts. Deliberately duplicated as a literal
 * so importing this module never drags in the react-i18next binding that
 * index.ts carries — keep the two in sync.
 */
const LANGUAGE_STORAGE_KEY = 'ferro.lang';

function resolveLanguage(explicit?: ErrorCopyLanguage): ErrorCopyLanguage {
  if (explicit === 'en' || explicit === 'es') return explicit;
  // 1. The shared i18next singleton, when someone initialized it.
  try {
    if (i18next.isInitialized && typeof i18next.language === 'string') {
      return i18next.language.toLowerCase().startsWith('es') ? 'es' : 'en';
    }
  } catch {
    // Singleton unusable — fall through.
  }
  // 2. The persisted preference (same key initI18n reads).
  try {
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved === 'es' || saved === 'en') return saved;
    }
  } catch {
    // Private-browsing / sandboxed contexts can throw — fall through.
  }
  return 'en';
}

/**
 * Map a raw server error string to copy safe to render.
 *
 *  - `null` / `undefined` / empty → generic fallback.
 *  - Prose (anything that is not a machine code) → returned UNTOUCHED.
 *  - Machine code in the dictionary → its human copy (en/es).
 *  - Machine code NOT in the dictionary → generic fallback. NEVER the
 *    raw input.
 */
export function errorCopy(
  input: string | null | undefined,
  lang?: ErrorCopyLanguage,
): string {
  const language = resolveLanguage(lang);
  const dict = DICTS[language];
  const generic = dict['generic'] ?? HARD_FALLBACK[language];
  if (input == null) return generic;
  const trimmed = input.trim();
  if (trimmed.length === 0) return generic;
  if (!isMachineCode(trimmed)) return input;
  // Bare single-word codes resolve their dictionary entry case-
  // insensitively ('Forbidden' → errors.forbidden). Snake_case codes are
  // already lowercase (regex-enforced) and prose never reaches this line,
  // so nothing else is ever case-folded.
  const key = BARE_MACHINE_CODES.has(trimmed.toLowerCase())
    ? trimmed.toLowerCase()
    : trimmed;
  return dict[key] ?? generic;
}
